import React, { useState, useEffect, useRef } from "react";

const Question = ({ data, selectedOption, onOptionClick }) => {
  const letters = ["A", "B", "C", "D"];
  const [animateIn, setAnimateIn] = useState(true);
  const [pendingOption, setPendingOption] = useState(null);
  const [answeredOption, setAnsweredOption] = useState(null);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(true);
  const answerStartRef = useRef(performance.now());

  useEffect(() => {
    setAnimateIn(true);
    setPendingOption(null);
    setAnsweredOption(null);
    setTimer(0);
    setTimerActive(true);
    answerStartRef.current = performance.now();
  }, [data]);

  useEffect(() => {
    if (!timerActive) return;

    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timerActive]);

  const formatTimer = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, "0")}` : `${seconds}`;
  };

  // correct_option arrives from the API as a number while option ids are
  // strings, so compare as strings to avoid a mismatch that breaks scoring.
  const isCorrectOption = (optionId) =>
    data != null && String(optionId) === String(data.correctAnswer);

  const handleOptionClick = (optionId) => {
    if (pendingOption || answeredOption) return;

    // Capture the answer time now, before the feedback animation delays.
    const elapsedMs = performance.now() - answerStartRef.current;

    setPendingOption(optionId);
    setTimerActive(false);

    setTimeout(() => {
      const correctSound = new Audio("/assets/sounds/correct.mp3");
      const wrongSound = new Audio("/assets/sounds/wrong.mp3");
      (isCorrectOption(optionId) ? correctSound : wrongSound)
        .play()
        .catch(() => {});

      setPendingOption(null);
      setAnsweredOption(optionId);

      setTimeout(() => {
        setAnimateIn(false);
        setTimeout(() => {
          onOptionClick(optionId, elapsedMs);
        }, 800); // allow fade-out animation to finish
      }, 2000);
    }, 1500); // delay before showing feedback and playing sound
  };

  const getOptionAnimation = (index) => {
    if (animateIn) {
      switch (index) {
        case 0: return "slide-in-top-left";
        case 1: return "slide-in-top-right";
        case 2: return "slide-in-bottom-left";
        case 3: return "slide-in-bottom-right";
        default: return "";
      }
    } else {
      switch (index) {
        case 0: return "slide-out-top-left";
        case 1: return "slide-out-top-right";
        case 2: return "slide-out-bottom-left";
        case 3: return "slide-out-bottom-right";
        default: return "";
      }
    }
  };

  const getOptionClass = (optionId) => {
    if (pendingOption === optionId) {
      return "pending";
    }
    if (answeredOption) {
      if (optionId === answeredOption) {
        return isCorrectOption(optionId) ? "correct" : "wrong";
      }
      if (isCorrectOption(optionId)) {
        return "correct";
      }
    }
    return "";
  };

  return (
    <div id="game-view">
      <div className={`question-wrapper ${animateIn ? "fade-in-down" : "fade-out-up"}`}>
        <div className="question-left-side"></div>
        <div id="question">{data.question}</div>
        <div className="question-right-side" id="timer">{formatTimer(timer)}</div>
      </div>

      <div id="options">
        {data.options.map((option, index) => (
          <button
            key={option.id}
            className={`option ${getOptionAnimation(index)} ${getOptionClass(option.id)} ${
              option.text.length > 55 ? "long" : ""
            }`}
            style={{ animationDelay: "0s" }}
            onClick={() => handleOptionClick(option.id)}
            disabled={!!pendingOption || !!answeredOption}
          >
            <div className="option-letter">
              <span>{letters[index]}</span>
            </div>
            <div className="option-text">{option.text}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Question;
